const img = (id, w = 1600) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

// All photography on this site is illustrative stock imagery representing the
// types of services LEDOS provides. It does not depict LEDOS employees,
// technicians, customers or completed LEDOS projects.
export const IMAGES = {
  serverRoom: img("photo-1558494949-ef010cbdcc31"),
  networkSwitch: img("photo-1544197150-b99a580bb7a8"),
  cabling: img("photo-1591808216268-ce0b82787efe"),
  technician: img("photo-1621905251189-08b45d6a269e"),
  cctv: img("photo-1557597774-9d273605dfa9"),
  payment: img("photo-1556740738-b6a63e27c4df"),
  engineer: img("photo-1581092918056-0c4c3acd3789"),
  deployment: img("photo-1581091226825-a6a2a5aee158"),
  signage: img("photo-1605810230434-7631ac76ec81"),
  datacenter: img("photo-1573164713988-8665fc963095"),
};

export const ILLUSTRATIVE_NOTE = "Representative of service capabilities";

export const CAPABILITIES = [
  "IT Infrastructure",
  "Structured Cabling",
  "ATM & POS",
  "Security Systems",
  "Digital Signage",
  "Technology Rollouts",
  "Field Support",
];

export const PROCESS_STEPS = [
  {
    num: "01",
    title: "Assess",
    text: "Understand the equipment, site, connectivity and project requirements.",
  },
  {
    num: "02",
    title: "Prepare",
    text: "Coordinate installation requirements and confirm site readiness.",
  },
  {
    num: "03",
    title: "Install",
    text: "Deploy hardware, cabling and supporting infrastructure.",
  },
  {
    num: "04",
    title: "Configure",
    text: "Configure equipment and connectivity according to project requirements.",
  },
  {
    num: "05",
    title: "Test",
    text: "Validate functionality, connectivity and installation quality.",
  },
  {
    num: "06",
    title: "Support",
    text: "Troubleshoot issues and provide post-installation technical support where required.",
  },
];

export const INDUSTRIES = [
  { name: "Retail", image: img("photo-1441986300917-64674bd600d8", 900) },
  { name: "Financial Services", image: img("photo-1526304640581-d334cdbbf45e", 900) },
  { name: "Corporate Offices", image: img("photo-1497366216548-37526070297c", 900) },
  { name: "Hospitality", image: img("photo-1566073771259-6a8506099945", 900) },
  { name: "Healthcare", image: img("photo-1519494026892-80bbd2d6fd0d", 900) },
  { name: "Warehousing & Logistics", image: img("photo-1553413077-190dd305871c", 900) },
  { name: "Commercial Facilities", image: img("photo-1486406146926-c627a92ad1ab", 900) },
  { name: "Multi-Location Businesses", image: img("photo-1586528116311-ad8dd3c8310d", 900) },
];

export const SERVICES = [
  {
    slug: "it-infrastructure-networking",
    num: "01",
    title: "IT Infrastructure & Network Services",
    short:
      "Establish, expand, upgrade or troubleshoot the physical network infrastructure your business runs on.",
    image: IMAGES.networkSwitch,
    intro:
      "Your network is the backbone of every transaction, call and connection in your business. LEDOS installs, configures and supports the physical network infrastructure that keeps locations connected — from routers, switches and servers to the wireless coverage and equipment that ties them together.",
    body: "Whether you are building out a new location, expanding an existing network, upgrading aging equipment or chasing down a connectivity problem, our field technicians work hands-on at your site to get infrastructure deployed correctly and running reliably.",
    capabilities: [
      "Network installation",
      "Network configuration",
      "Routers & switches",
      "Server installation",
      "WiFi installation & wireless coverage",
      "Network equipment installation",
      "Network troubleshooting",
      "Connectivity troubleshooting",
      "Infrastructure upgrades",
      "Equipment replacement",
      "On-site technical services",
    ],
  },
  {
    slug: "structured-cabling",
    num: "02",
    title: "Structured Cabling",
    short:
      "The foundation of reliable business technology — installed, terminated, tested and labeled.",
    image: IMAGES.cabling,
    intro:
      "Every dependable network starts in the walls, ceilings and racks. LEDOS installs the structured cabling that connects network equipment to the endpoints your business depends on — cleanly routed, properly terminated and clearly documented.",
    body: "From new data cable runs to reorganizing and managing existing infrastructure, we treat cabling as the foundation it is: done right the first time, tested before handover and labeled so the next technician — yours or ours — knows exactly what they are looking at.",
    capabilities: [
      "Data cable runs",
      "Network cabling",
      "Cable installation & routing",
      "Cable management",
      "Cable termination",
      "Testing & certification of runs",
      "Labeling & documentation",
      "Infrastructure preparation",
      "Connections between network equipment and endpoints",
    ],
  },
  {
    slug: "atm-pos-services",
    num: "03",
    title: "ATM & Payment Technology Services",
    short:
      "Deployment and field support for the transaction infrastructure your customers touch every day.",
    image: IMAGES.payment,
    intro:
      "Payment and transaction equipment has to work — every time a customer reaches for it. LEDOS provides hands-on deployment and field service for ATMs and point-of-sale systems: installation, connectivity, peripherals, replacement and troubleshooting.",
    body: "We handle the physical and technical side of transaction technology at your locations — setting equipment in place, connecting it, configuring it and keeping it operational so your staff can focus on customers instead of hardware.",
    capabilities: [
      "ATM installation & equipment deployment",
      "ATM connectivity setup",
      "ATM peripheral installation",
      "POS installation & deployment",
      "POS connectivity",
      "Hardware replacement",
      "Configuration",
      "Troubleshooting",
      "Field support",
    ],
  },
  {
    slug: "security-camera-sensor-installation",
    num: "04",
    title: "Security, Camera & Sensor Installation",
    short:
      "Technical installation and infrastructure for surveillance and sensor technology.",
    image: IMAGES.cctv,
    intro:
      "Cameras and sensors are only as reliable as their mounting, cabling and connectivity. LEDOS handles the technical installation side of security technology — positioning equipment correctly, connecting it to your network and configuring it to perform.",
    body: "From single-site CCTV deployments to multi-camera installations with supporting network infrastructure, we take responsibility for the physical and network layers that make security technology dependable.",
    capabilities: [
      "Security-camera installation",
      "CCTV deployment",
      "Camera mounting",
      "Connectivity & configuration",
      "Sensor installation",
      "Supporting network infrastructure",
      "Troubleshooting",
    ],
  },
  {
    slug: "digital-signage-display",
    num: "05",
    title: "Digital Signage & Display Solutions",
    short:
      "Commercial displays installed, connected and configured for customer-facing communication.",
    image: IMAGES.signage,
    intro:
      "Digital signage carries your message to customers and staff — when it is mounted right, connected right and configured right. LEDOS installs and supports the display technology businesses use for customer communication, information displays, advertising and internal communications.",
    body: "We take care of the physical installation, media devices, display and network connectivity, configuration and ongoing troubleshooting — so screens go up cleanly and stay on.",
    capabilities: [
      "Digital signage installation",
      "Commercial display installation",
      "Screen mounting",
      "Media-device installation",
      "Display & network connectivity",
      "Digital display configuration",
      "Signage troubleshooting",
      "Technology replacement and upgrades",
    ],
  },
  {
    slug: "equipment-installation-configuration",
    num: "06",
    title: "IT Equipment Installation & Configuration",
    short:
      "Broad-range installation and configuration of the business technology you have already chosen.",
    image: IMAGES.datacenter,
    intro:
      "You have the equipment — LEDOS gets it installed and working. We install and configure a broad range of business technology: servers, switches, routers, network hardware, peripherals, displays, appliances and connected devices.",
    body: "Our role is flexible by design. Tell us what needs to be deployed and where, and we bring the hands-on technical execution to rack it, connect it, configure it and validate it in your environment.",
    capabilities: [
      "Servers",
      "Switches & routers",
      "Network hardware",
      "IT peripherals",
      "Displays",
      "Technology appliances",
      "Connected devices",
      "General business IT equipment",
    ],
  },
  {
    slug: "technology-rollouts",
    num: "07",
    title: "Technology Deployment & Multi-Site Rollouts",
    short:
      "Coordinated deployment projects — from one location to every location you operate.",
    image: IMAGES.deployment,
    intro:
      "Rolling out technology across locations takes more than technicians — it takes coordination. LEDOS supports organizations deploying new technology, replacing equipment or upgrading infrastructure across one site or many, with a structured approach from planning through completion.",
    body: "New-location technology installation, hardware refreshes, equipment replacement, network, POS, ATM, digital-signage and camera deployments — each project is managed through planning, site readiness, installation, configuration, testing, documentation, issue resolution and project completion.",
    capabilities: [
      "New-location technology installation",
      "Hardware refreshes & equipment replacement",
      "Technology & infrastructure upgrades",
      "Network deployments",
      "POS & ATM deployments",
      "Digital-signage rollouts",
      "Camera deployments",
      "Planning & site readiness",
      "Testing & documentation",
      "Issue resolution & project completion",
    ],
  },
  {
    slug: "troubleshooting-maintenance-field-support",
    num: "08",
    title: "Troubleshooting, Maintenance & Field Support",
    short:
      "Getting business-critical technology operational again — efficiently and on-site.",
    image: IMAGES.engineer,
    intro:
      "When technology stops working, everything built on it stops too. LEDOS provides technical troubleshooting and field support focused on one goal: getting business-critical equipment operational again, efficiently.",
    body: "From connectivity problems and hardware diagnostics to equipment replacement, configuration correction and preventive maintenance where applicable, our field support extends past installation day into the life of your technology.",
    capabilities: [
      "Technical troubleshooting",
      "Connectivity troubleshooting",
      "Hardware diagnostics",
      "Equipment replacement",
      "Configuration correction",
      "Network troubleshooting",
      "On-site support",
      "Preventive maintenance where applicable",
      "Technology repair coordination",
      "Post-installation support",
    ],
  },
];
